/**
 * Which deployment is this bundle running as? (the `environment` tag on every
 * Sentry event / transaction).
 *
 * WHY THIS IS NOT JUST `import.meta.env.MODE`
 * -------------------------------------------
 * `MODE` is `"production"` for ANY production build — `vite build` says nothing
 * about *where* the bundle is deployed. We have two hosted deployments built the
 * exact same way (the `morrowos-web` Pages project on app.morrowos.com and the
 * `morrowos-web-staging` Pages project on morrowos-web-staging.pages.dev), so
 * tagging from `MODE` filed real production incidents and staging noise into one
 * `environment: production` bucket, which is unsplittable after the fact. Real
 * Sentry events proved it: `environment: production` with
 * `https://morrowos-web-staging.pages.dev/` as their URL.
 *
 * Setting `VITE_SENTRY_ENVIRONMENT` on the staging Pages project fixes it, and an
 * explicit value still wins here — but the code must not DEPEND on someone
 * remembering to set it. So when it is absent we derive the environment from
 * things that actually differ between the deployments:
 *
 *  1. the baked backend URL (`VITE_HTTP_URL`) — the honest build-time signal.
 *     Staging points at `api-staging.morrowos.com`, production at
 *     `api.morrowos.com`. A bundle cannot be staging and talk to the production
 *     API, so this is the part that cannot drift.
 *  2. the page's own hostname at runtime — a `*.pages.dev` host is never the
 *     production deployment (production is served on the custom domain
 *     app.morrowos.com), so it is at worst staging and at best a branch preview.
 *
 * `VITE_HOSTED_APP_CHANNEL` was considered and rejected: it carries the DESKTOP
 * download channel ("latest" / "nightly", see branding.ts), not which backend a
 * web deployment is wired to, and it is unset on both Pages projects.
 *
 * Kept as a pure function (no `import.meta.env`, no `location`) so the rule is
 * unit-testable — see environment.test.ts.
 */

export interface TelemetryEnvironmentInput {
  /** `VITE_SENTRY_ENVIRONMENT` — an explicit override always wins. */
  readonly explicit?: string | undefined;
  /** `VITE_HTTP_URL` — the backend this bundle was built to talk to. */
  readonly backendUrl?: string | undefined;
  /** `location.hostname` of the page, when running in a browser. */
  readonly hostname?: string | undefined;
  /** `import.meta.env.MODE` — the build mode, used only to spot non-production builds. */
  readonly mode?: string | undefined;
}

/** Hosts that only ever mean "someone's machine". */
const LOCAL_HOSTNAMES = new Set(["localhost", "127.0.0.1", "0.0.0.0", "::1", "[::1]"]);

function hostOf(url: string): string | null {
  try {
    return new URL(url).hostname.toLowerCase();
  } catch {
    return null;
  }
}

/** True when any dot-separated label of the host is (or starts with) "staging". */
function mentionsStaging(host: string): boolean {
  return host
    .split(".")
    .some(
      (label) => label === "staging" || label.startsWith("staging-") || /-staging$/.test(label),
    );
}

function isLocalHost(host: string): boolean {
  return LOCAL_HOSTNAMES.has(host) || host.endsWith(".local") || host.endsWith(".ts.net");
}

/**
 * Resolve the Sentry/PostHog `environment` tag for this bundle.
 *
 * Returns one of `production` | `staging` | `preview` | `development` (or the
 * build mode / explicit override verbatim).
 */
export function resolveTelemetryEnvironment(input: TelemetryEnvironmentInput): string {
  // 1. An explicit env var is the owner's word — never second-guess it.
  const explicit = input.explicit?.trim();
  if (explicit) return explicit;

  // 2. A non-production BUILD is never a deployment: `vite dev` and the test
  //    runner report "development" / "test", and that is already the truth.
  const mode = input.mode?.trim();
  if (mode && mode !== "production") return mode;

  const backendHost = input.backendUrl?.trim() ? hostOf(input.backendUrl.trim()) : null;
  const pageHost = input.hostname?.trim().toLowerCase() ?? "";

  // 3. The backend URL decides first: it is baked at build time and a staging
  //    bundle cannot be pointed at the production API.
  if (backendHost && mentionsStaging(backendHost)) return "staging";

  // 4. A *.pages.dev host is never production — production is app.morrowos.com.
  //    `<project>.pages.dev` is a project's own alias (staging, if it says so);
  //    anything with an extra label in front is a per-branch preview deploy.
  if (pageHost === "pages.dev" || pageHost.endsWith(".pages.dev")) {
    const labels = pageHost.split(".");
    if (labels.length > 3) return "preview";
    return mentionsStaging(pageHost) ? "staging" : "preview";
  }

  // 5. A production-mode bundle served from a developer's machine (`vite preview`,
  //    a local static serve) is still development.
  if (pageHost && isLocalHost(pageHost)) return "development";
  if (!pageHost && backendHost && isLocalHost(backendHost)) return "development";

  // 6. Nothing says otherwise: the real thing.
  return "production";
}
